// Tournament Management Module - TypeScript Models
// Mirrors backend view models and enums for the Tournament Management feature.

// ============================================================================
// Enums (numeric, matching backend C# enum values)
// ============================================================================

export enum TournamentStatus {
  Upcoming = 0,
  Active = 1,
  Completed = 2,
  Cancelled = 3,
}

export enum TournamentRecurrency {
  OneTime = 0,
  Weekly = 1,
  BiWeekly = 2,
  Monthly = 3,
  Quarterly = 4,
  Yearly = 5,
}

export enum TournamentFormat {
  League = 0,
  Knockout = 1,
  GroupStage = 2,
}

export enum MatchStatus {
  Scheduled = 0,
  InProgress = 1,
  Completed = 2,
  Closed = 3,
  UnderRevision = 4,
  Cancelled = 5,
}

export enum MatchType {
  Outdoor = 0,
  Indoor = 1,
}

export enum MatchFormat {
  FiveAside = 0,
  SevenAside = 1,
  NineAside = 2,
  ElevenAside = 3,
}

export enum MatchRole {
  HeadReferee = 0,
  AssistantReferee = 1,
  FourthOfficial = 2,
  Commissioner = 3,
}

export enum RefereeCertification {
  NationalLevelA = 0,
  AFCElite = 1,
  FIFAInternational = 2,
}

export enum RefereeAvailability {
  Available = 0,
  Busy = 1,
  Unavailable = 2,
}

export enum AssignmentResponse {
  Pending = 0,
  Accepted = 1,
  Declined = 2,
}

export enum AssignmentAction {
  Assigned = 0,
  Reassigned = 1,
  Removed = 2,
  AcceptedByReferee = 3,
  DeclinedByReferee = 4,
  NotificationFailed = 5,
}

export enum AgeCategory {
  U06 = 0, U07 = 1, U08 = 2, U09 = 3, U10 = 4,
  U11 = 5, U12 = 6, U13 = 7, U14 = 8, U15 = 9,
  U16 = 10, U17 = 11, U18 = 12, U19 = 13,
  U21 = 14, Senior = 15,
}

// ============================================================================
// Tournament Views
// ============================================================================

export interface TournamentView {
  id: number;
  name: string;
  description?: string;
  venue: string;
  startDate: string;
  endDate: string;
  participants: number;
  status: TournamentStatus;
  format: TournamentFormat;
  recurrency: TournamentRecurrency;
  matchCount: number;
  createdOn: string;
}

export interface TournamentListView {
  items: TournamentView[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface TournamentStatsView {
  totalTournaments: number;
  activeTournaments: number;
  upcomingTournaments: number;
  completedTournaments: number;
  totalMatches: number;
}

// ============================================================================
// Match Views
// ============================================================================

export interface MatchView {
  id: number;
  tournamentId: number;
  tournamentName: string;
  team1Name: string;
  team2Name: string;
  location: string;
  pitch?: string;
  ageCategory?: number | string;  // API sends string, normalize with resolveEnum
  division?: string;
  scheduledDateTime: string;
  /** True when only the date is known and the kickoff time is unset — render "Time TBD". */
  scheduledTimeUnknown: boolean;
  status: MatchStatus;
  type: MatchType;
  format: MatchFormat;
  team1Score?: number;
  team2Score?: number;
  extraTimeTeam1Score?: number;
  extraTimeTeam2Score?: number;
  penaltyTeam1Score?: number;
  penaltyTeam2Score?: number;
  winnerTeamName?: string;
  completionComments?: string;
  attachmentUrl?: string;
  attachmentUploadedById?: string;
  attachmentUploadedByName?: string;
  attachmentUploadedOn?: string;
  requiredCertification?: RefereeCertification;
  refereeTeamMembers: RefereeTeamMemberView[];
}

export interface RefereeTeamMemberView {
  id: number;
  userId: string;
  userName: string;
  role: MatchRole;
  workPoints: number;
  workPointOverride?: number;
  workPointBreakdown?: string;
  phone?: string;
  email?: string;
  matchRating?: number;
}

export interface MatchListView {
  items: MatchView[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  availableLocations: string[];
  availablePitches: string[];
  availableDivisions: string[];
}

export interface ImportResultView {
  importedCount: number;
  skippedCount: number;
  errors: string[];
}

// ============================================================================
// Referee Views
// ============================================================================

export interface RefereeView {
  id: number;
  userId: string;
  name: string;
  photoUrl?: string;
  certification: RefereeCertification;
  phone?: string;
  email?: string;
  location?: string;
  rating: number;
  totalMatches: number;
}

export interface RefereeListView {
  items: RefereeView[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  availableCount: number;
}

export interface RefereeRecommendationView {
  refereeProfileId: number;
  name: string;
  photoUrl?: string;
  certification: RefereeCertification;
  fitScore: number;
  fitReasons: string[];
  rank: number;
  estimatedWorkPoints: number;
}

export interface RefereeScheduleView {
  date: string;
  status: RefereeAvailability;
  matchId?: number;
  matchDescription?: string;
  matchTime?: string;
  matchLocation?: string;
  matchStatus?: string;
  reason?: string;
}

export interface AssignmentHistoryView {
  id: number;
  refereeName: string;
  action: AssignmentAction;
  previousRefereeName?: string;
  reason?: string;
  notificationSent: boolean;
  createdOn: string;
}

export interface AssignmentResultView {
  matchId: number;
  refereeProfileId: number;
  action: AssignmentAction;
  notificationSent: boolean;
  message: string;
}

export interface AssignmentResponseResultView {
  matchId: number;
  newStatus: AssignmentResponse;
  message: string;
}

export interface RespondToAssignmentInput {
  matchId: number;
  reason?: string;
}

// ============================================================================
// Work Point Views
// ============================================================================

export interface WorkPointRuleView {
  id: number;
  headRefereeBasePoints: number;
  assistantRefereeBasePoints: number;
  fourthOfficialBasePoints: number;
  fiveAsidePoints: number;
  sevenAsidePoints: number;
  nineAsidePoints: number;
  elevenAsidePoints: number;
  indoorBonus: number;
  outdoorBonus: number;
  weekendHolidayBonus: number;
  nightMatchBonus: number;
  nightMatchThresholdHour: number;
  earlyMorningBonus: number;
  earlyMorningThresholdHour: number;
  highRatingThreshold: number;
  highRatingBonus: number;
  midRatingThreshold: number;
  midRatingBonus: number;
  minPointsPerMatch: number;
  maxPointsPerMatch: number;
  pointToCurrencyRate: number;
  effectiveFrom: string;
  effectiveTo?: string;
  isActive: boolean;
}

export interface WorkPointEstimateView {
  estimatedPoints: number;
  estimatedCurrencyAmount: number;
  breakdown: string;
  baseRolePoints: number;
  formatPoints: number;
  typeBonus: number;
  timeBonus: number;
  performanceBonus: number;
  rawSubtotal: number;
  minPointsPerMatch: number;
  maxPointsPerMatch: number;
  pointToCurrencyRate: number;
  isNonWorkingDay: boolean;
}

export interface WorkPointBreakdownView {
  refereeTeamMemberId: number;
  refereeName: string;
  role: MatchRole;
  totalPoints: number;
  baseRolePoints: number;
  formatPoints: number;
  typeBonus: number;
  timeBonus: number;
  performanceBonus: number;
  calculatedPoints: number;
  isOverridden: boolean;
  currencyAmount: number;
}

export interface CompensationReportView {
  lines: RefereeCompensationLine[];
  totalWorkPoints: number;
  totalCurrencyAmount: number;
  startDate: string;
  endDate: string;
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface RefereeCompensationLine {
  refereeUserId: string;
  refereeName: string;
  matchCount: number;
  totalWorkPoints: number;
  totalCurrencyAmount: number;
}

export interface PayrollEntriesResultView {
  entriesCreated: number;
  refereesProcessed: number;
  totalAmount: number;
  startDate: string;
  endDate: string;
  message?: string;
  entries: PayrollEntryLine[];
}

export interface PayrollEntryLine {
  refereeUserId: string;
  refereeName: string;
  totalWorkPoints: number;
  currencyAmount: number;
  matchCount: number;
  variablePayId: number;
}

// ============================================================================
// My Assignments / Self-Service Views
// ============================================================================

export interface MyAssignmentsView {
  items: MyAssignmentItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface MyAssignmentItem {
  matchId: number;
  tournamentId: number;
  tournamentName: string;
  team1Name: string;
  team2Name: string;
  location: string;
  scheduledDateTime: string;
  matchStatus: MatchStatus;
  role: MatchRole;
  workPoints: number;
  responseStatus: number;
  responseDeadline: string | null;
}

export interface MyWorkPointsSummaryView {
  totalWorkPoints: number;
  totalCurrencyAmount: number;
  matchCount: number;
  recentMatches: MyAssignmentItem[];
}

// ============================================================================
// Input Interfaces (for API calls)
// ============================================================================

export interface CreateTournamentInput {
  name: string;
  description?: string;
  venue: string;
  startDate: string;
  endDate: string;
  participants: number;
  recurrency?: TournamentRecurrency;
  format?: TournamentFormat;
}

export interface UpdateTournamentInput {
  id: number;
  name: string;
  description?: string;
  venue: string;
  startDate: string;
  endDate: string;
  participants: number;
  status: TournamentStatus;
  recurrency: TournamentRecurrency;
  format?: TournamentFormat;
}

export interface TournamentListQuery {
  search?: string;
  status?: TournamentStatus;
  startDateFrom?: string;
  startDateTo?: string;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

export interface CreateMatchInput {
  tournamentId: number;
  team1Name: string;
  team2Name: string;
  location: string;
  pitch?: string;
  ageCategory?: AgeCategory;
  division?: string;
  scheduledDateTime: string;
  scheduledTimeUnknown?: boolean;
  type: MatchType;
  format: MatchFormat;
  requiredCertification?: RefereeCertification;
  refereeTeamMembers?: { userId: string; role: MatchRole; workPointOverride?: number }[];
}

export interface UpdateMatchInput {
  id: number;
  team1Name: string;
  team2Name: string;
  location: string;
  pitch?: string;
  ageCategory?: AgeCategory;
  division?: string;
  scheduledDateTime: string;
  scheduledTimeUnknown?: boolean;
  type: MatchType;
  format: MatchFormat;
  requiredCertification?: RefereeCertification;
  refereeTeamMembers?: { userId: string; role: MatchRole; workPointOverride?: number }[];
}

export interface CompleteMatchInput {
  matchId: number;
  team1Score: number;
  team2Score: number;
  extraTimeTeam1Score?: number;
  extraTimeTeam2Score?: number;
  penaltyTeam1Score?: number;
  penaltyTeam2Score?: number;
  comments?: string;
  refereeRatings?: { refereeTeamMemberId: number; rating: number }[];
}

export interface UpdateMatchStatusInput {
  matchId: number;
  newStatus: MatchStatus;
  reason?: string;
}

export interface MatchListQuery {
  tournamentId?: number;
  search?: string;
  status?: MatchStatus;
  location?: string;
  pitch?: string;
  ageCategory?: AgeCategory;
  division?: string;
  completedOnly?: boolean;
  /** HR/Admin-only — filter to matches where a specific referee is assigned. */
  refereeUserId?: string;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

export interface AssignRefereeInput {
  matchId: number;
  refereeProfileId: number;
  role: MatchRole;
  notifyParties: boolean;
  reason?: string;
  workPointOverride?: number;
}

export interface CreateRefereeProfileInput {
  userId: string;
  certification: RefereeCertification;
}

export interface UpdateRefereeProfileInput {
  id: number;
  certification: RefereeCertification;
  rating?: number;
}

export interface RefereeListQuery {
  search?: string;
  certification?: RefereeCertification;
  page?: number;
  pageSize?: number;
}

// ============================================================================
// Work Point Input Interfaces
// ============================================================================

export interface CreateWorkPointRuleInput {
  headRefereeBasePoints: number;
  assistantRefereeBasePoints: number;
  fourthOfficialBasePoints: number;
  fiveAsidePoints: number;
  sevenAsidePoints: number;
  nineAsidePoints: number;
  elevenAsidePoints: number;
  indoorBonus: number;
  outdoorBonus: number;
  weekendHolidayBonus: number;
  nightMatchBonus: number;
  nightMatchThresholdHour: number;
  earlyMorningBonus: number;
  earlyMorningThresholdHour: number;
  highRatingThreshold: number;
  highRatingBonus: number;
  midRatingThreshold: number;
  midRatingBonus: number;
  minPointsPerMatch: number;
  maxPointsPerMatch: number;
  pointToCurrencyRate: number;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface UpdateWorkPointRuleInput extends CreateWorkPointRuleInput {
  id: number;
}

export interface WorkPointEstimateQuery {
  type: MatchType;
  format: MatchFormat;
  role: MatchRole;
  refereeProfileId?: number;
  scheduledDateTime?: string;
}

export interface CompensationReportQuery {
  startDate: string;
  endDate: string;
  refereeUserId?: string;
  tournamentId?: number;
}

export interface MyAssignmentsQuery {
  page?: number;
  pageSize?: number;
}

export interface MyWorkPointsQuery {
  startDate?: string;
  endDate?: string;
}

// ============================================================================
// Standings
// ============================================================================

export interface StandingEntry {
  teamName: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  points: number;
}

// ============================================================================
// Bracket
// ============================================================================

export interface BracketMatchView {
  matchId: number;
  team1Name: string;
  team2Name: string;
  status: MatchStatus;
  bracketPosition: number;
  team1Score?: number;
  team2Score?: number;
  scheduledDateTime?: string;
  winner?: string;
}

export interface BracketRound {
  roundNumber: number;
  roundName: string;
  matches: BracketMatchView[];
}

export interface BracketView {
  tournamentId: number;
  tournamentName: string;
  rounds: BracketRound[];
}

export interface GenerateBracketInput {
  teamNames: string[];
  defaultLocation: string;
  firstRoundDateTime: string;
  hoursBetweenMatches: number;
  daysBetweenRounds: number;
  format: MatchFormat;
  type: MatchType;
  allowRegenerate?: boolean;
  ageCategory?: AgeCategory;
  division?: string;
  pitch?: string;
}

// ============================================================================
// Live Score & Reschedule
// ============================================================================

export interface RescheduleMatchInput {
  matchId: number;
  newScheduledDateTime: string;
  reason?: string;
}

export interface UpdateLiveScoreInput {
  matchId: number;
  team1Score: number;
  team2Score: number;
}

// ============================================================================
// Bulk Import
// ============================================================================

export interface BulkImportMatchRow {
  day: string;
  venue: string;
  pitch?: string;
  time: string;
  ageCategory?: string;
  division?: string;
  team1Name: string;
  team2Name: string;
  /** Optional raw value from a STATUS column (e.g. "Cancelled"). */
  status?: string;
  commissioner?: string;
  headReferee?: string;
  assistantReferee1?: string;
  assistantReferee2?: string;
  fourthOfficial?: string;
}

export interface BulkImportMatchesInput {
  tournamentId: number;
  weekStartDate: string;
  defaultFormat: MatchFormat;
  defaultType: MatchType;
}

export interface BulkImportPreviewView {
  rows: BulkImportMatchRow[];
  totalRows: number;
  validRows: number;
  warnings: string[];
  availableVenues: string[];
  availableAgeCategories: string[];
  availableDivisions: string[];
}

export enum BulkImportRowAction {
  New = 0,
  Update = 1,
  Skip = 2,
  Error = 3,
}

export interface BulkImportRowResult {
  rowIndex: number;
  action: BulkImportRowAction;
  /** Blocking error message (rare). */
  message?: string;
  /** Non-blocking notes for a row that was still imported. */
  warnings: string[];
}

export interface MatchBulkImportResultView {
  created: number;
  updated: number;
  skipped: number;
  /** Rows imported with one or more warnings. */
  warningCount: number;
  errors: string[];
  rowResults: BulkImportRowResult[];
}

export interface BulkImportAnalyzeResultView {
  rowResults: BulkImportRowResult[];
  newCount: number;
  updateCount: number;
  skipCount: number;
  errorCount: number;
  /** Rows (New/Update) that carry one or more warnings. */
  warningCount: number;
  errors: string[];
}

export interface BulkImportMatchesRequest {
  input: BulkImportMatchesInput;
  rows: BulkImportMatchRow[];
}

// ============================================================================
// Utility
// ============================================================================

/** Resolves a numeric or string enum value, returning the number. */
export function resolveEnum<T extends Record<string, number | string>>(
  enumObj: T,
  value: unknown
): number | undefined {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const found = enumObj[value as keyof T];
    if (typeof found === 'number') return found;
  }
  return undefined;
}
